import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, PlusCircle, CheckCircle, X, ArrowRight, RefreshCw } from 'lucide-react';
import PageContainer from '../components/PageContainer';
import { api } from '../lib/api';

export default function AddIncome({ loadIncome, incomeData, allSources, addCustomSource, toggleSidebar }) {
  const navigate = useNavigate();

  const [mode, setMode] = useState('manual');

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0], amount: '', source: 'Swiggy/Zomato', description: ''
  });

  const [importText, setImportText] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const [loading, setLoading] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [newSourceName, setNewSourceName] = useState('');

  const safeAllSources = allSources || ['Swiggy/Zomato', 'Other'];

  const particles = useMemo(() => Array.from({ length: 12 }).map((_, i) => ({
    id: i, size: Math.random() * 3, left: Math.random() * 100, top: Math.random() * 100,
    duration: Math.random() * 20 + 10
  })), []);

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (formData.amount <= 0) return alert("Amount must be positive.");

    setLoading(true);
    try { await api.post(`/income/add`, formData); loadIncome(); navigate('/transactions'); }
    catch (error) { alert('Could not save entry.'); }
    setLoading(false);
  };

  const handleAnalyze = async () => {
    if (!importText && !importFile) return;
    setIsAnalyzing(true);

    const form = new FormData();
    if (importFile) form.append('file', importFile);
    let url = `/income/ai-import`;
    if (importText && !importFile) url += `?text_data=${encodeURIComponent(importText)}`;

    try {
      const res = await api.post(url, importFile ? form : null);
      if (res.data.error) { alert(res.data.error); setIsAnalyzing(false); }
      else { setTimeout(() => { setPreviewData(res.data.preview); setIsAnalyzing(false); }, 1000); }
    } catch (e) { alert("Could not process data."); setIsAnalyzing(false); }
  };

  const handleBulkSave = async () => {
    setLoading(true);
    try { await api.post(`/income/add-bulk-entries`, previewData); loadIncome(); navigate('/transactions'); }
    catch (e) { alert("Save failed"); }
    setLoading(false);
  };

  const handlePreviewChange = (i, f, v) => {
    const updated = [...previewData]; updated[i][f] = v; setPreviewData(updated);
  };

  const handleAddSource = () => {
    if (addCustomSource(newSourceName)) {
      setFormData(prev => ({ ...prev, source: newSourceName.trim() })); setNewSourceName(''); setShowSourceModal(false);
    }
  };

  const handleSampleData = async () => {
    if (!window.confirm("⚠️ This will ERASE your current data and load 3 months of diverse test data. Continue?")) return;
    setLoading(true);
    try { await api.post(`/income/sample`); loadIncome(); navigate('/'); } catch (e) { alert("Failed."); }
    setLoading(false);
  };

  return (
    <PageContainer
      title="Record Income"
      subtitle="Track your earnings"
      toggleSidebar={toggleSidebar}
      actions={
        <button className="btn-header-ghost" onClick={handleSampleData} disabled={loading}>
          {loading ? 'Loading...' : <><RefreshCw size={14} /> Load Test Data</>}
        </button>
      }
    >

      <div className="center-glass-layout">

        <div className="glass-toggle">
          <button className={`toggle-option ${mode === 'manual' ? 'active' : ''}`} onClick={() => setMode('manual')}>Manual Entry</button>
          <button className={`toggle-option ${mode === 'ai' ? 'active' : ''}`} onClick={() => setMode('ai')}>AI Smart Import</button>
        </div>

        <AnimatePresence mode='wait'>
          {mode === 'manual' ? (
            <motion.div key="manual" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="glass-card-hero">
              <form onSubmit={handleSingleSubmit}>
                <div className="hero-amount-wrapper">
                  <label>Amount Received</label>
                  <div className="input-flex">
                    <span>₹</span>
                    <input
                      type="number"
                      min="0"
                      autoComplete="off"
                      onKeyDown={(e) => { if (["-", "e", "E"].includes(e.key)) e.preventDefault(); }}
                      value={formData.amount}
                      onChange={e => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      autoFocus
                      required
                    />
                  </div>
                </div>

                <div className="form-grid">
                  <div className="glass-field">
                    <label>Date</label>
                    <input type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} required />
                  </div>
                  <div className="glass-field">
                    <label>Source</label>
                    <div className="select-row">
                      <select value={formData.source} onChange={e => setFormData({ ...formData, source: e.target.value })}>
                        {safeAllSources.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button type="button" className="btn-icon-glass" onClick={() => setShowSourceModal(true)}>+</button>
                    </div>
                  </div>
                </div>
                <div className="glass-field full">
                  <label>Note</label>
                  <input type="text" placeholder="Description (Optional)" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
                </div>

                <button type="submit" className="btn-submit-glass" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Transaction'}
                </button>
              </form>
            </motion.div>
          ) : (
            <motion.div key="ai" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="glass-card-ai">
              <div className="bg-particles">
                {particles.map(p => <motion.div key={p.id} className="glow-dot" style={{ width: p.size, height: p.size, left: `${p.left}%`, top: `${p.top}%` }} animate={{ opacity: [0.3, 0.8, 0.3] }} transition={{ duration: p.duration, repeat: Infinity }} />)}
              </div>

              {!previewData.length ? (
                <div className="ai-input-area">
                  <div className="ai-head">
                    <h3><Bot size={18} className="icon-glow" /> Financial Assistant</h3>
                    <p>Paste text or upload a file. I can calculate daily recurring income.</p>
                  </div>

                  <div className={`glass-textarea-wrap ${isAnalyzing ? 'processing' : ''}`}>
                    <textarea
                      placeholder="Example: 'Made 500 daily on Uber for the last 10 days' or 'Freelance payment of 1200 yesterday'"
                      value={importText}
                      onChange={e => setImportText(e.target.value)}
                      disabled={isAnalyzing}
                    />

                    <div className="action-bar">
                      <div className="upload-group">
                        <input type="file" id="file-up" accept=".csv,.xlsx" onChange={e => setImportFile(e.target.files[0])} hidden />
                        <label htmlFor="file-up" className="btn-clip" title="Supports CSV, Excel">
                          {importFile ? <CheckCircle size={16} color="#10B981" /> : <PlusCircle size={18} />}
                        </label>
                        {importFile && <span className="file-name">{importFile.name} <button onClick={() => setImportFile(null)}><X size={10} /></button></span>}
                      </div>

                      <button className="btn-analyze" onClick={handleAnalyze} disabled={isAnalyzing}>
                        {isAnalyzing ? 'Analyzing...' : 'Analyze'} <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="ai-preview-area">
                  <div className="preview-top">
                    <div>
                      <strong>{previewData.length} Transactions Found</strong>
                      <span>Total: ₹{previewData.reduce((a, b) => a + (Number(b.amount) || 0), 0).toLocaleString()}</span>
                    </div>
                    <div className="top-actions">
                      <button className="btn-text" onClick={() => { setPreviewData([]); setImportText(''); }}>Cancel</button>
                      <button className="btn-solid" onClick={handleBulkSave} disabled={loading}>{loading ? 'Saving...' : 'Save All'}</button>
                    </div>
                  </div>

                  <div className="preview-grid-scroll">
                    <table className="glass-table-rows">
                      <thead><tr><th>Date</th><th>Source</th><th>Description</th><th>Amount</th><th></th></tr></thead>
                      <tbody>
                        {previewData.map((row, i) => (
                          <tr key={i}>
                            <td width="130"><input type="date" value={row.date} onChange={e => handlePreviewChange(i, 'date', e.target.value)} /></td>
                            <td>
                              <div className="cell-flex">
                                <select value={row.source} onChange={e => handlePreviewChange(i, 'source', e.target.value)}>
                                  {safeAllSources.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <button onClick={() => setShowSourceModal(true)} className="tiny-plus">+</button>
                              </div>
                            </td>
                            <td><input type="text" value={row.description} onChange={e => handlePreviewChange(i, 'description', e.target.value)} /></td>
                            <td width="100">
                              <input
                                type="number"
                                min="0"
                                onKeyDown={(e) => { if (["-", "e", "E"].includes(e.key)) e.preventDefault(); }}
                                value={row.amount}
                                onChange={e => handlePreviewChange(i, 'amount', e.target.value)}
                                className="font-bold"
                              />
                            </td>
                            <td width="40"><button className="del-btn" onClick={() => setPreviewData(previewData.filter((_, x) => x !== i))}><X size={14} /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>{showSourceModal && <motion.div className="modal-overlay-improved"><motion.div className="modal-improved"><div className="modal-header-improved"><h3>New Source</h3><button className="btn-icon-close" onClick={() => setShowSourceModal(false)}><X size={20} /></button></div><div className="modal-body-improved"><input type="text" className="form-input-improved" value={newSourceName} onChange={e => setNewSourceName(e.target.value)} autoFocus /></div><div className="modal-actions-improved"><button className="btn-large btn-primary" onClick={handleAddSource}>Add</button></div></motion.div></motion.div>}</AnimatePresence>
    </PageContainer>
  );
}
